import type { MouseEvent } from 'react';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { ADMIN_IMPLEMENTATION, READY_FOR_ADMIN } from '@/data/migration/model';
import useRipplingMigration from '@/rippling/hooks/useRipplingMigration';
import { RIPPLING_MIGRATION_SCHEDULE_A_CALL_URL } from '@/rippling/util/constants';
import { navigateToRippling } from '@/rippling/util/navigateToRippling';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import wiwBerryIcon from '@/shared/components/assets/images/wiw-berry-app-icon.svg';
import Button from '@/shared/ui/Button';
import { posthogCapture } from '@/shared/vendor/posthog';
import { useWiwSelector } from '@/store';

import classnames from 'classnames';
import moment from 'moment-timezone';
import pluralize from 'pluralize';

import '@/rippling/assets/styles/RipplingMigrationBanner.scss';

export default function RipplingMigrationBanner() {
  const bannerRef = useRef<HTMLElement>(null);

  const history = useHistory();
  const authUser = useWiwSelector(getAuthUser);
  const authAccount = useWiwSelector(getAuthAccount);
  const { migrationActive, migrationStep, migrationWindowEnd, missedMigrationDate } = useRipplingMigration();
  const daysUntil = migrationWindowEnd
    ? migrationWindowEnd.clone().startOf('day').diff(moment().startOf('day'), 'days')
    : 0;

  const showBanner = migrationActive && authUser.canManage();

  useEffect(() => {
    const updateBannerHeight = (height: number) => {
      document.documentElement.style.setProperty('--rippling-banner-height', `${height}px`);
    };

    if (!showBanner) {
      updateBannerHeight(0);
      return;
    }

    if (!bannerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (bannerRef.current) {
          const height = bannerRef.current.offsetHeight;
          updateBannerHeight(height);
        }
      });
    });

    resizeObserver.observe(bannerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [showBanner]);

  const renderLogos = () => {
    return (
      <div className="brand-logos">
        <div className="svg-icon">
          <svg
            className="wiw-logo"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>WIW Logo</title>
            <path
              className="background"
              d="M0 12.8C0 8.31958 0 6.07937 0.871948 4.36808C1.63893 2.86278 2.86278 1.63893 4.36808 0.871948C6.07937 0 8.31958 0 12.8 0H19.2C23.6804 0 25.9206 0 27.6319 0.871948C29.1372 1.63893 30.3611 2.86278 31.1281 4.36808C32 6.07937 32 8.31958 32 12.8V19.2C32 23.6804 32 25.9206 31.1281 27.6319C30.3611 29.1372 29.1372 30.3611 27.6319 31.1281C25.9206 32 23.6804 32 19.2 32H12.8C8.31958 32 6.07937 32 4.36808 31.1281C2.86278 30.3611 1.63893 29.1372 0.871948 27.6319C0 25.9206 0 23.6804 0 19.2V12.8Z"
              fill="#51A33D"
            />
            <path
              className="logo"
              d="M16 7C20.9705 7 25 11.0293 25 16C25 20.9705 20.9705 25 16 25C11.0295 25 7 20.9705 7 16C7 11.0293 11.0295 7 16 7ZM16 8.53223C11.8755 8.53223 8.53223 11.8755 8.53223 16C8.53228 20.1244 11.8755 23.4678 16 23.4678C20.1245 23.4678 23.4677 20.1244 23.4678 16C23.4678 11.8755 20.1245 8.53223 16 8.53223ZM18.75 17.7236C19.0291 17.7238 19.2549 17.9414 19.2549 18.21V18.7686C19.2549 19.0371 19.0291 19.2547 18.75 19.2549H12.1016C11.8223 19.2549 11.5957 19.0372 11.5957 18.7686V18.21C11.5957 17.9413 11.8223 17.7236 12.1016 17.7236H18.75ZM20.2812 15.043C20.5605 15.043 20.7871 15.2606 20.7871 15.5293V16.0879C20.7871 16.3566 20.5605 16.5742 20.2812 16.5742H13.6328C13.3537 16.574 13.1279 16.3564 13.1279 16.0879V15.5293C13.1279 15.2608 13.3537 15.0432 13.6328 15.043H20.2812ZM18.75 12.3613C19.0289 12.3615 19.2546 12.5793 19.2549 12.8477V13.4072C19.2549 13.6758 19.0291 13.8933 18.75 13.8936H12.1016C11.8223 13.8936 11.5957 13.6759 11.5957 13.4072V12.8477C11.5959 12.5792 11.8224 12.3613 12.1016 12.3613H18.75Z"
              fill="white"
            />
          </svg>
        </div>
        <div className="svg-icon">
          <svg
            className="arrow-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Arrow</title>
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M21.923 12.3815C22.025 12.1375 22.025 11.8615 21.923 11.6175C21.872 11.4945 21.799 11.3845 21.706 11.2925L15.707 5.2925C15.317 4.9025 14.684 4.9025 14.293 5.2925C13.902 5.6835 13.902 6.3165 14.293 6.7065L18.586 10.9995H3C2.447 10.9995 2 11.4475 2 11.9995C2 12.5515 2.447 12.9995 3 12.9995H18.586L14.293 17.2925C13.902 17.6835 13.902 18.3165 14.293 18.7065C14.488 18.9025 14.744 18.9995 15 18.9995C15.256 18.9995 15.512 18.9025 15.707 18.7065L21.706 12.7075C21.799 12.6145 21.872 12.5045 21.923 12.3815Z"
              fill="#333333"
            />
          </svg>
        </div>
        <img width="32" height="32" src={wiwBerryIcon} alt="" />
      </div>
    );
  };

  const renderDuration = (days: number) => {
    if (days < 1) {
      return '0 days';
    }
    return `${days} ${pluralize('day', days)}`;
  };

  const renderContent = () => {
    if (missedMigrationDate) {
      return (
        <div className="migration-content stacked">
          <strong>Action required: Complete {authAccount.company} upgrade to When I Work by Rippling</strong>
          <p>
            To continue using When I Work, you need to finish your setup in Rippling today. Need help? A specialist can
            walk you through the upgrade.
          </p>
        </div>
      );
    }

    switch (migrationStep) {
      case READY_FOR_ADMIN:
        return (
          <div className="migration-content">
            <strong>You have {renderDuration(daysUntil)} left to upgrade to When I Work by Rippling.</strong>
            <p>All the same features you rely on—plus more—at the same cost, now on Rippling's platform.</p>
          </div>
        );
      case ADMIN_IMPLEMENTATION:
        return (
          <div className="migration-content stacked">
            <strong>Heads up: You will log into When I Work through Rippling in {renderDuration(daysUntil)}.</strong>
            <p>
              Your schedules and settings carry over, your bill stays the same, and you &amp; your team can log in with
              your existing When I Work credentials.
            </p>
          </div>
        );
    }
  };

  const trackAction = (action: string) => () => {
    posthogCapture(`rippling_account_migration_banner.${action}`);
  };

  const handleLearnMore = () => {
    posthogCapture('rippling_account_migration_banner.learn_more');
    history.push('/wiw-by-rippling');
  };

  const handleAuxClick = (action: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 1) {
      trackAction(action)();
    }
  };

  const renderCTA = () => {
    if (missedMigrationDate) {
      return (
        <div className="migration-cta">
          <a
            href={RIPPLING_MIGRATION_SCHEDULE_A_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-md btn-secondary"
            onClick={trackAction('contact_specialist')}
            onAuxClick={handleAuxClick('contact_specialist')}
          >
            Schedule your call today
          </a>
          <button
            type="button"
            className="btn btn-md btn-secondary"
            onClick={() => {
              trackAction('see_rippling')();
              void navigateToRippling({ newTab: true });
            }}
          >
            Open Rippling
          </button>
        </div>
      );
    }

    switch (migrationStep) {
      case READY_FOR_ADMIN:
        return (
          <div className="migration-cta">
            <Button color="primary" onClick={handleLearnMore}>
              Find out more
            </Button>
          </div>
        );

      case ADMIN_IMPLEMENTATION:
        return (
          <div className="migration-cta">
            <Button color="secondary" onClick={handleLearnMore}>
              Find out more
            </Button>
            <button
              type="button"
              onClick={() => {
                trackAction('see_rippling')();
                void navigateToRippling({ newTab: true });
              }}
              className="btn btn-md btn-secondary"
            >
              Log into Rippling
            </button>
          </div>
        );
    }
  };

  if (!showBanner) {
    return;
  }

  return (
    <section
      ref={bannerRef}
      className={classnames('rippling-migration-banner', {
        louder: migrationStep === ADMIN_IMPLEMENTATION && !missedMigrationDate,
        loudest: missedMigrationDate,
      })}
    >
      <div className="banner-layout">
        {renderLogos()}
        {renderContent()}
        {renderCTA()}
      </div>
    </section>
  );
}
